<template lang="pug">
div
  v-menu(v-model="show" rounded="lg" allow-overflow :close-on-content-click="false" max-height="345")
    template(#activator="{ on }")
      v-sheet.text-xs.round-xl.clickable(flat outlined :class="isError ? 'error-border-menu mt-2' : 'border-menu'" :height="height" @click="fetchProducts")
        v-row.fill-height(no-gutters align="center")
          v-col.pl-3
            div.neutral400--text(:class="inputTextSize") Search by name or group name 
          v-col.pr-3.pl-1.shrink
            v-progress-circular(v-if="isFetching" :width="2" :size="12" color="primary" indeterminate)
            v-icon.ml-1(v-else :size="iconSize" style="margin-right: -4px") $yzi-search-sm
      div.text-xxs.danger500--text.ml-3.mt-1.mb-2(v-show="isError") {{ errorMessage }}

    .text-xs.white
      .search-input
        v-text-field.mb-2.mx-4.text-sm.round-xl(v-model="itemName" outlined :placeholder="inventoryOnly ? 'Search inventory' : 'Search inventory and services'" hide-details autofocus dense @input="searchStart")
        v-divider
      div.d-flex.justify-center(v-if="isFetching")
        v-progress-linear(:width="3" rounded color="primary" indeterminate)
      .d-flex.align-center.justify-space-between.px-4.py-3
        .font-semibold.mr-6 Existing treatments
        y-pagination(v-model="page" :pageSize="pageSize" :count="count" :loading="isFetching" @input="pageChanged") 
      div(v-for="item in productItems" @click="onSelectItem(item)")
        .d-flex.flex-column.py-3.px-4(:class="[{ 'hover-bg':!item.disabled, 'disabled-item':item.disabled, 'clickable': !item.disabled }]")
          .d-flex.align-center(style="gap: 8px;")
            .text-sm.font-normal(v-if="item.uom") {{ item.name }}  ({{item.uom}})
            .text-sm.font-normal(v-else) {{ item.name }}
            yz-badge(v-if="item.branch_for" x-small color="blue-100" text-color="blue-500" border) HQ
            .yz-regular-body-2xs.text-neutral-500.no-wrap(v-if="item.sku && isInventoryItem(item)") {{ item.sku }}
            yz-badge(v-if="item.supplier" color="neutral-200" text-color="neutral-700" border border-color="neutral-300") {{ item.supplier }}
          v-row(no-gutters align="center" style="gap: 12px;")
            v-col.shrink
              .d-flex.align-center.no-wrap
                template(v-if="hasStock(item)")
                  v-icon.mr-1(size="11" color="success") $yzi-circle
                  .text-xs.font-normal.neutral500--text {{ getStockLabel(item) }}
                template(v-else)
                  v-icon.mr-1(size="11" color="danger") $yzi-circle
                  .text-xs.font-normal.neutral500--text {{ $t('out_of_stock') }}
            v-col.shrink
              .d-flex.align-center.no-wrap
                v-icon.mr-1(size="13") {{ getTypeIcon(item) }}
                .text-xs.font-normal.neutral500--text {{ item.type | capitalize }}
            v-col.shrink
              template(v-if="item.groups && item.groups.length > 0")
                .d-flex.align-center.no-wrap
                  v-icon.mr-1(size="13") $yzi-folder
                  .text-xs.font-normal.neutral500--text {{ item.groups.join(', ') | capitalize }}
            v-col.shrink
              template(v-if="item.price_range && item.price_range.length > 0")
                .d-flex.align-center.no-wrap
                  v-icon.mr-1(size="13") $yzi-tag-02
                  template(v-for="(price, idx) in item.price_range")
                    div.text-xs.font-normal.neutral500--text(:key="`pr-${idx}`") {{ price | displayPrice($currency) }}
                    div.mx-1.neutral500--text(v-if="item.price_range.length > 1 && !idx") -


          .text-xxs.font-normal.italic.danger500--text(v-if="item.hasError") {{ item.errorMessage }}
        v-divider
      template(v-if="!productItems.length")
        .px-4.py-3 {{ $t('no_result_match') }}
</template>

<script>
import { debounce } from '@/utils'
import { mapGetters, mapActions } from 'vuex'

export default {
  props: {
    isOTC: {
      type: Boolean
    },
    isError: {
      type: Boolean
    },
    errorMessage: {
      type: String,
      default: ''
    },
    size: {
      type: String,
      default: 'sm'
    },
    inputTextSize: {
      type: String,
      default: 'text-sm'
    },
    inventoryOnly: {
      type: Boolean,
      default: false
    },
    allowOutOfStock: {
      type: Boolean,
      default: false
    }
  },
  data: () => ({
    isFetching: false,
    show: false,
    itemName: null,
    page: 1,
    pageSize: 10,
    count: 0,
    productItems: [],
    initialized: false
  }),
  computed: {
    ...mapGetters({
      company: 'getCompany',
      hqSelectedCompany: 'hq/getSelectedCompany',
      isHq: 'hq/getIsHq'
    }),
    height () {
      switch (this.size) {
        case 'xxs':
          return 28
        case 'xs':
          return 32
        case 'sm':
          return 36
        case 'md':
          return 40
        default:
          return 36
      }
    },
    iconSize () {
      switch (this.size) {
        case 'xxs':
          return 12
        case 'xs':
          return 14
        case 'sm':
          return 16
        case 'md':
          return 18
        default:
          return 16
      }
    },
  },
  watch: {},
  methods: {
    ...mapActions(['showMessage']),
    fetchProducts () {
      this.isFetching = true
      let params = {
        page: this.page,
        page_size: this.pageSize,
        search: this.itemName || null,
        status: 'published'
      }
      if (this.inventoryOnly) {
        params.relations = 'medication'
      }
      const companySlug = this.isHq ? this.hqSelectedCompany.slug : this.company.slug
      this.$axios.get(`/clinics/${companySlug}/treatment_products`, { params })
        .then(resp => {
          const newData = resp.data.results.map(result => {
            return {
              ...result,
              sku: result.sku,
              disabled: this.isDisabled(result),
              hasError: this.hasError(result),
              errorMessage: this.getErrorMessage(result),
            }
          })
          this.productItems = newData
          this.count = resp.data.count
        })
        .catch(() => {
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        })
        .finally(() => {
          this.isFetching = false
          this.initialized = true
          this.show = true
        })
    },
    searchStart: debounce(function () {
      this.page = 1
      this.fetchProducts()
    }, 750),
    pageChanged () {
      this.fetchProducts()
    },
    onSelectItem (item) {
      if (item.disabled) {
        return
      }
      if (item.medication) {
        if (!item.medication.instruction_enabled) {
          item.medication.dosage_unit = null
          item.medication.dosage_value = null
          item.medication.frequency = null
          item.medication.duration = null
          item.medication.instruction = null
          item.medication.precaution = null
          item.medication.indication = null
        }
      }
      this.itemName = null
      this.show = false
      this.$emit('added', item)
    },
    hasStock(item) {
      if (item.type === 'bundle') {
        if (item.bundle.available_qty === null) {
          return true
        } else {
          return item.bundle.available_qty > 0
        }
      }
      if (!item.inventory_enabled) {
        return true
      }
      if (item.inventory_qty > 0) {
        return true
      }
      return false
    },
    getTypeIcon (item) {
      if (item.medication) {
        return '$yzi-colors'
      }
      if (item.type === 'documents') {
        return '$yzi-file-06'
      }
      return 'mdi-briefcase-outline'
    },
    hasError (item) {
      return item.type === 'documents' && this.isOTC
    },
    getErrorMessage (item) {
      if (this.hasError(item)) {
        return 'Medical document cannot be added for OTC'
      }
      return null
    },
    getStockLabel (item) {
      if (item.type === 'bundle') {
        if (item.bundle.available_qty === null) return this.$t('in_stock')
        return `On hand: ${item.bundle.available_qty}`
      }
      if (!item.inventory_enabled) {
        return this.$t('in_stock')
      }
      return `On hand: ${item.inventory_qty}`
    },
    isInventoryItem (item) {
      return ['medication', 'equipment', 'consumable'].includes(item.type)
    },
    isDisabled (item) {
      if (this.allowOutOfStock) return this.hasError(item)
      return !this.hasStock(item) || this.hasError(item)
    }
  }
}
</script>

<style scoped lang="scss">
.border-menu {
  border: 1px solid var(--v-basic500);
  box-shadow: none;
}
.error-border-menu {
  border: 1px solid var(--v-danger500);
  box-shadow: none;
}
.search-input {
  position: sticky;
  top: 0px;
  background-color: #FFFFFF !important;
  border-color: #FFFFFF !important;
  z-index: 4;
  padding-top: 8px;
}
.disabled-item {
  background-color: var(--v-neutral300);
  color: var(--v-neutral500);
}
</style>